// @flow

import * as React from 'react';

import clsx from 'clsx';
import capitalize from 'lodash/capitalize';

import { selectTaxesDisplaySetting } from '@gandi/authentication';
import { selectCartIsWorking } from '@gandi/cart';
import type { ApplicationSummary } from '@gandi/redux-resource-simplehosting';
import { useTranslator } from '@gandi/translate';
import {
  Base,
  Button,
  Text,
  UnorderedList,
  TypoListItem as ListItem,
  SrOnly,
} from '@gandi/ui';

import { useAppSelector } from 'src/hooks/useAppSelector';

import { ProductContext } from '../ProductContext';
import { useInstancesPrices } from '../modules/prices';
import { selectCartHasHostingProduct } from '../modules/selectors';
import type { InstancePrices } from '../types';

import styles from './Card.scss';

export type Size = 'starter' | 'advanced' | 'pro' | 'business' | 'storage';

type Props = {
  size: string,
  argumentatives: ?Array<string>,
  currency: string,
  infos: InstancePrices,
  onClick: (evt: SyntheticMouseEvent<>) => mixed,
  isPopular: boolean,
  application?: ApplicationSummary,
  packId: string,
};

const useTaglines = (applicationName?: string) => {
  const { __ } = useTranslator();

  if (!applicationName) {
    return {
      starter: __('For blogs or portfolios.'),
      advanced: __('Perfect to launch any website.'),
      pro: __('Tailored for professional websites.'),
      business: __('Our most complete solution for your company.'),
    };
  }
  if (applicationName === 'nextcloud') {
    return {
      advanced: __('Store your photos and files while assuring your privacy.'),
      pro: __(
        'Synchronize files across devices and share them with your team.',
      ),
      business: __(
        'Advanced features and extensions to work efficiently with your team.',
      ),
    };
  }
  if (applicationName === 'prestashop') {
    return {
      advanced: __('To start your own e-boutique.'),
      pro: __("For your small business's online store."),
      business: __(
        'For a website with a lot of traffic, optimized for additional modules.',
      ),
    };
  }

  return {
    starter: __('For blogs or portfolios.'),
    advanced: __('Perfect to launch any website.'),
    pro: __('Tailored for professional websites.'),
    business: __('Ideal for intense traffic.'),
  };
};

const usePrice = (instancePrices: InstancePrices): number => {
  const displayTaxes = useAppSelector(selectTaxesDisplaySetting);

  return displayTaxes
    ? instancePrices.price_after_taxes
    : instancePrices.price_before_taxes;
};

const useNormalPrice = (instancePrices: InstancePrices): void | number => {
  const displayTaxes = useAppSelector(selectTaxesDisplaySetting);

  return displayTaxes
    ? instancePrices.normal_price_after_taxes
    : instancePrices.normal_price_before_taxes;
};

function useConfigurationDetails(): void | {
  [key: string]: string,
  ...
} {
  const { instancesPrices, isLoaded: instancesPricesLoaded } =
    useInstancesPrices();

  const { __ } = useTranslator();

  if (!instancesPricesLoaded) return undefined;

  const GB = __('<for-translators-use>gigabytes</for-translators-use>GB');
  const MB = __('<for-translators-use>megabytes</for-translators-use>MB');

  const configurations = {};

  instancesPrices.forEach(price => {
    const { product } = price;
    const { name, params } = product;
    const { cpu, ram } = params;

    if (!cpu || !ram) {
      return;
    }

    // $FlowIgnore[prop-missing]
    configurations[name] = __(
      '%(cpu_count)s CPU/RAM up to %(ram)s %(ram_unit)s',
      {
        cpu_count: cpu,
        ram: ram < 1024 ? ram : ram / 1024,
        ram_unit: ram < 1024 ? MB : GB,
      },
    );
  });

  return configurations;
}

function Card({
  size,
  argumentatives,
  onClick,
  currency,
  infos,
  isPopular,
  application,
  packId,
}: Props): React.Node {
  const { __, formatPrice } = useTranslator();
  const taglines = useTaglines(application?.name);
  const { productId, plan } = React.useContext(ProductContext);
  const price = usePrice(infos);
  const normalPrice = useNormalPrice(infos);
  const configurationDetails = useConfigurationDetails();
  const hasTaxes = infos.taxes.length ? infos.taxes[0].rate !== 0 : false;
  const displayTaxes = useAppSelector(selectTaxesDisplaySetting);

  const isCartBusy = useAppSelector(selectCartIsWorking);

  const storageInfos = infos.product.params.storage;
  const product = useAppSelector(state =>
    selectCartHasHostingProduct(state, {
      productId,
      size,
      application: application?.name,
      packId,
      min_duration: plan === 'monthly' ? 1 : 12,
    }),
  );

  const sizeLabel = capitalize(size);

  const isYearly = plan === 'yearly';

  return (
    <Base
      flex
      flexCol
      borderRadius={isPopular ? '2x' : '1x'}
      className={clsx(styles.root, { [styles.mostPopularCard]: isPopular })}
    >
      <Base
        size="small"
        borderStartRadius={isPopular ? '2x' : undefined}
        align="center"
        transform="uppercase"
        color="white"
        visibility={isPopular ? 'visible' : 'hidden'}
        weight="semibold"
        paddingY="xxsmall"
      >
        {__('Most popular')}
      </Base>
      <Base
        tagName="article"
        flexGrow1
        flex
        flexCol
        backgroundColor="white"
        padding="xsmall"
        gap="xsmall"
        borderRadius="1x"
        className={styles.card}
      >
        <Base className={styles.uppercard}>
          <Base flex flexJustifyBetween flexWrap gap="xsmall">
            <Base tagName="h3" margin="none">
              {sizeLabel}
            </Base>
          </Base>
          <div className={styles.description}>
            <Text size="small" color="light">
              {
                // $FlowFixMe[invalid-computed-prop]
                __(taglines[size])
              }
            </Text>
          </div>
        </Base>
        {
          // $FlowIgnore[sketchy-null-number]
          !!normalPrice && (
            <Base size="big">
              <SrOnly>{__('Old price:')}</SrOnly>
              <Base tagName="s">
                <Text weight="semibold">
                  {formatPrice(normalPrice * infos.min_duration, currency)}
                </Text>
                /
                <Text size="small" weight="semibold">
                  {isYearly ? __('year') : __('month')}
                </Text>
              </Base>
            </Base>
          )
        }
        <Base>
          {
            // $FlowIgnore[sketchy-null-number]
            !!normalPrice && <SrOnly>{__('Discounted price:')}</SrOnly>
          }
          <Text size="bigger" weight="semibold">
            {formatPrice(price * infos.min_duration, currency)}
          </Text>
          /
          <Text size="small" weight="semibold">
            {isYearly ? __('year') : __('month')}
          </Text>{' '}
          {hasTaxes && (
            <>
              {' '}
              <Text size="small" color="light">
                {displayTaxes ? __('incl. taxes') : __('excl. taxes')}
              </Text>
            </>
          )}
        </Base>
        <Base color="success" size="small">
          {__('No hosting yet? Pay yearly and save 50%% on your 1st hosting')}
        </Base>

        <div>
          <Text weight="semibold">
            {__('Storage %(storageSize)s GB', {
              storageSize: storageInfos,
            })}
          </Text>
        </div>
        {configurationDetails && (
          <div>
            <Text color="light" size="small">
              {configurationDetails[size]}
            </Text>
          </div>
        )}
        <Base paddingY="xsmall">
          {!!product && (
            <Button
              block
              small
              intent="success"
              iconBefore="checkmark"
              style={{ pointEvents: 'none' }}
            >
              {__(
                '<for-translators-use>No "shopping"</for-translators-use>Added to cart',
              )}
            </Button>
          )}
          {!product && (
            <Button block small disabled={isCartBusy} onClick={onClick}>
              {__('Choose %(size)s', {
                size: sizeLabel,
              })}
            </Button>
          )}
        </Base>
        {!!argumentatives?.length && (
          <UnorderedList icon="miniCheckmark" iconTheme="green">
            {argumentatives.map(arg => (
              <ListItem key={arg}>
                <Text size="small">{arg}</Text>
              </ListItem>
            ))}
          </UnorderedList>
        )}
      </Base>
    </Base>
  );
}

export default Card;
